// ===========================================
// BUSTED! BLACKJACK - ANIMATION TIMING CONFIG
// ===========================================
// Configurable animation durations for cross-platform support
// Each platform (Web, Electron, Mobile, Unity, Unreal) can override
//
// IMPORTANT: This is the SINGLE SOURCE OF TRUTH for animation timing.
// Both server (autoplay pacing) and client (animation playback) use these values.
// ===========================================
// ===========================================
// CHIP BREAKDOWN UTILITY
// ===========================================
// Converts a dollar amount into optimal chip denominations.
// Used by both client (animation) and server (timing calculation).
// Standard denominations (for backwards-compatible breakdownChips)
const CHIP_DENOMINATIONS = [10000, 5000, 1000, 500, 100, 25, 5, 1];
// Extended denominations for chip breakdown
// NOTE: Only include denominations that have chip textures on the client!
// Future high-value chips (50k, 100k, 500k, 1M) are commented out until textures are added.
// If a denomination is included here but has no texture, the chip will be SKIPPED
// during animation and the counter will not update correctly.
const EXTENDED_DENOMINATIONS = [/* 1000000, 500000, 100000, 50000, */ 10000, 5000, 1000, 500, 100, 25, 5, 1];
// Maximum total chips for animation (to prevent performance issues)
const MAX_TOTAL_CHIPS = 100;
/**
 * Break down a dollar amount into optimal chip denominations.
 * Returns array of chip values (e.g., 125 → [100, 25])
 */
export function breakdownChips(amount) {
    const chips = [];
    let remaining = Math.floor(amount); // Handle any fractional amounts
    for (const denom of CHIP_DENOMINATIONS) {
        while (remaining >= denom) {
            chips.push(denom);
            remaining -= denom;
        }
    }
    // For fractional amounts (e.g., $0.50), show a $1 chip as visual placeholder
    if (chips.length === 0 && amount > 0) {
        chips.push(1);
    }
    return chips;
}
/**
 * Calculate chip count for a given amount (for timing calculations)
 */
export function getChipCount(amount) {
    return breakdownChips(amount).length;
}
/**
 * Create a server-authoritative chip breakdown for payout events.
 *
 * This function:
 * 1. Breaks down the amount using extended denominations (up to $1M chips)
 * 2. Caps total chips at MAX_TOTAL_CHIPS (100) by reducing ONLY the largest denomination
 * 3. Preserves chip diversity (smaller denominations kept intact)
 * 4. Adjusts per-chip value for capped denomination so counter reaches correct total
 *
 * Example: $4,174,300 payout
 * - Raw breakdown: 3x$100 + 4x$1000 + 417x$10k = 424 chips
 * - Capped: 3x$100 + 4x$1000 + 93x$10k = 100 chips
 * - $10k perChipValue adjusted: (417 × 10000) ÷ 93 = $44,839
 *
 * @param amount - Total payout amount
 * @returns ChipBreakdown with all 12 denominations explicit (zeroes for unused)
 */
export function createCappedChipBreakdown(amount) {
    // Initialize all denominations with zero qty and face value
    const breakdown = {
        denom1: { qty: 0, value: 1 },
        denom5: { qty: 0, value: 5 },
        denom25: { qty: 0, value: 25 },
        denom100: { qty: 0, value: 100 },
        denom500: { qty: 0, value: 500 },
        denom1000: { qty: 0, value: 1000 },
        denom5000: { qty: 0, value: 5000 },
        denom10000: { qty: 0, value: 10000 },
        denom50000: { qty: 0, value: 50000 },
        denom100000: { qty: 0, value: 100000 },
        denom500000: { qty: 0, value: 500000 },
        denom1000000: { qty: 0, value: 1000000 },
        totalChips: 0,
    };
    if (amount <= 0) {
        return breakdown;
    }
    // Map denomination values to breakdown keys
    const denomKeyMap = {
        1: 'denom1',
        5: 'denom5',
        25: 'denom25',
        100: 'denom100',
        500: 'denom500',
        1000: 'denom1000',
        5000: 'denom5000',
        10000: 'denom10000',
        50000: 'denom50000',
        100000: 'denom100000',
        500000: 'denom500000',
        1000000: 'denom1000000',
    };
    // Break down amount using extended denominations
    let remaining = Math.floor(amount);
    const denomCounts = new Map();
    for (const denom of EXTENDED_DENOMINATIONS) {
        const count = Math.floor(remaining / denom);
        if (count > 0) {
            denomCounts.set(denom, count);
            remaining -= count * denom;
        }
    }
    // Handle fractional amounts
    if (denomCounts.size === 0 && amount > 0) {
        denomCounts.set(1, 1);
    }
    // Calculate total chips
    let totalChips = 0;
    for (const count of denomCounts.values()) {
        totalChips += count;
    }
    // If under limit, populate breakdown directly
    if (totalChips <= MAX_TOTAL_CHIPS) {
        for (const [denom, count] of denomCounts) {
            const key = denomKeyMap[denom];
            if (key) {
                breakdown[key] = { qty: count, value: denom };
            }
        }
        breakdown.totalChips = totalChips;
        return breakdown;
    }
    // Need to cap - find the largest denomination with chips
    let largestDenom = 0;
    for (const denom of EXTENDED_DENOMINATIONS) {
        if (denomCounts.has(denom) && denomCounts.get(denom) > 0) {
            largestDenom = denom;
            break;
        }
    }
    if (largestDenom === 0) {
        // Edge case: no chips found
        breakdown.totalChips = 0;
        return breakdown;
    }
    // Calculate chips needed from smaller denoms
    let smallerDenomsTotal = 0;
    for (const [denom, count] of denomCounts) {
        if (denom < largestDenom) {
            smallerDenomsTotal += count;
        }
    }
    // Cap largest denomination to fit within MAX_TOTAL_CHIPS
    const originalLargestCount = denomCounts.get(largestDenom) || 0;
    const cappedLargestCount = Math.max(1, MAX_TOTAL_CHIPS - smallerDenomsTotal);
    const totalValueOfLargest = originalLargestCount * largestDenom;
    const adjustedValuePerChip = totalValueOfLargest / cappedLargestCount;
    // Populate breakdown
    for (const [denom, count] of denomCounts) {
        const key = denomKeyMap[denom];
        if (!key)
            continue;
        if (denom === largestDenom) {
            // Capped denomination with adjusted value
            breakdown[key] = { qty: cappedLargestCount, value: adjustedValuePerChip };
        }
        else {
            // Uncapped denomination with face value
            breakdown[key] = { qty: count, value: denom };
        }
    }
    breakdown.totalChips = smallerDenomsTotal + cappedLargestCount;
    return breakdown;
}
export const DEFAULT_UI_DELAY_CONFIG = {
    insuranceChipWait: 300, // 300ms - from PixiTablePage onInsuranceAccepted
    doubleDownChipWait: 400, // 400ms - from PixiTablePage onDoubleDown
    chipConsolidationSwap: 200, // 200ms - from PixiTablePage handleChipClick
};
export const DEFAULT_STAGGER_CONFIG = {
    // Chip staggers - standardized to 80ms for all chip movements
    chipPlacement: 80, // 80ms - between chips when placing bet
    chipPayout: 80, // 80ms - between chips when paying winnings
    chipCollection: 80, // 80ms - between chips when collecting to rail
    chipToDealer: 80, // 80ms - between chips when dealer collects losses
    chipMidRoundBust: 80, // 80ms - between chips on mid-round bust
    // Card staggers
    cardRake: 150, // 150ms - from cardAnimations.ts ARC_CONFIG.staggerDelay
    // Sidebet/Insurance staggers - standardized to 80ms
    sidebetChip: 80, // 80ms - sidebet chip stagger
    insuranceChip: 80, // 80ms - between insurance chip animations
    // UI staggers
    doubleDownChip: 80, // 80ms - from PixiTablePage onDoubleDown
    betConfirmChip: 80, // 80ms - from PixiTablePage onBetConfirmed
};
// ===========================================
// DEFAULT WEB TIMING (in milliseconds)
// ===========================================
//
// ⚠️ DEPRECATED - MARKED FOR DELETION ⚠️
//
// This constant should NOT be used as the source of truth for animation timing.
// The authoritative animation values are:
//   1. Server database: `server_config` table (category: 'animations')
//   2. Client: Received from server via `animation_config` socket message on table join
//
// Why this exists (temporarily):
//   - Backend serverConfig.service.ts uses these as fallbacks if DB is unavailable
//   - Legacy code paths may still reference these values
//
// Migration path:
//   1. Ensure all animation durations come from server events (event.duration field)
//   2. Remove references to DEFAULT_WEB_TIMING from:
//      - Backend: serverConfig.service.ts (use DB values only, fail if unavailable)
//      - Frontend: Any direct imports (use animationConfigStore instead)
//   3. Delete this constant entirely
//
// Values below are historical and may be outdated. DO NOT rely on them.
// ===========================================
export const DEFAULT_WEB_TIMING = {
    // Betting Phase (from chipAnimations.ts, unifiedChipAnimations.ts)
    betStaged: 200, // Chip staged animation
    betConfirmed: 200, // Chip flight to kitty
    betUnstaged: 150, // Chip returns from staging
    // Dealing Phase (from cardAnimations.ts, pixi-v2/constants.ts)
    handBoxAppear: 300, // Hand box slides in
    cardDeal: 400, // Player card deal (was 250, actual ~400-500ms)
    cardDealDealer: 315, // Dealer card deal (slightly faster)
    cardDealInterval: 0, // Events fire back-to-back, no interval
    cardFlip: 250, // Card flip animation
    scoreUpdate: 100, // Score badge update
    // Player Turn
    turnIndicatorShow: 200, // Turn beam animation
    actionButtonsShow: 150, // Button fade-in
    actionTaken: 100, // Action feedback
    cardHit: 400, // Hit card animation (same as deal)
    doubleDown: 500, // Double down chip + card
    splitHands: 600, // Split animation (from useGameEvents.ts)
    // Special Outcomes (from useGameEvents.ts calculateBatchTimeout)
    bust: 500, // Bust feedback
    blackjack: 800, // Blackjack celebration
    surrender: 500, // Surrender visual (from useGameEvents.ts)
    // Insurance (from pixi/registry/constants.ts)
    insurancePrompt: 300, // Panel appearance
    insuranceDecision: 200, // Decision feedback
    insuranceResolve: 1000, // Resolution (from useGameEvents.ts - includes reveal)
    insurancePlacement: 250, // Chip moves to insurance spot
    // Dealer Turn (from useGameEvents.ts calculateBatchTimeout)
    holeCardReveal: 500, // Hole card flip (was 400)
    dealerHit: 400, // Dealer card deal
    dealerHitInterval: 200, // Pause between dealer hits
    dealerStand: 200, // Stand indication
    dealerBust: 600, // Bust animation
    dealerBlackjack: 600, // Blackjack animation
    // Resolution (from useGameEvents.ts - 800ms includes stagger for typical payout)
    handOutcomeShow: 100, // Outcome label
    chipFlight: 200, // Base single chip flight
    chipsPaid: 800, // Full payout animation (base + stagger)
    chipsCollected: 800, // Full collection animation (base + stagger)
    chipStackUpdate: 200, // Stack visual update
    // Sidebet Animations (from PixiGameTableV1.tsx)
    sidebetPlaced: 200, // Sidebet chip placement (rail to icon)
    sidebetChipsPaid: 400, // Sidebet payout (dealer to icon: 200ms + icon to rail: 200ms)
    sidebetChipsCollected: 200, // Sidebet collection (icon to dealer tray - single hop)
    // Round Transitions (from cardAnimations.ts ARC_CONFIG)
    roundComplete: 500, // Cleanup
    cardsCleared: 450, // Rake duration (ARC_CONFIG.duration)
    roundStart: 300, // New round
    bettingOpened: 400, // Betting open
    // Global
    globalSpeedMultiplier: 1.0,
};
// ===========================================
// FAST MODE TIMING (for testing/impatient users)
// ===========================================
//
// ⚠️ DEPRECATED - MARKED FOR DELETION ⚠️
// See DEFAULT_WEB_TIMING comments above. This is also deprecated.
// Fast mode should be implemented via server config, not client constants.
//
export const FAST_WEB_TIMING = {
    ...DEFAULT_WEB_TIMING,
    cardDeal: 150,
    cardDealDealer: 120,
    cardDealInterval: 0,
    cardFlip: 100,
    cardHit: 150,
    dealerHit: 150,
    dealerHitInterval: 50,
    chipsPaid: 300,
    chipsCollected: 300,
    sidebetPlaced: 200,
    sidebetChipsPaid: 250,
    sidebetChipsCollected: 250,
    cardsCleared: 200,
    globalSpeedMultiplier: 0.5,
};
export const FAST_STAGGER_CONFIG = {
    chipPlacement: 15,
    chipPayout: 30,
    chipCollection: 20,
    chipToDealer: 30,
    chipMidRoundBust: 25,
    cardRake: 75,
    sidebetChip: 25,
    insuranceChip: 25,
    doubleDownChip: 30,
    betConfirmChip: 30,
};
export const FAST_UI_DELAY_CONFIG = {
    insuranceChipWait: 150,
    doubleDownChipWait: 200,
    chipConsolidationSwap: 100,
};
// ===========================================
// CINEMATIC MODE (slower, more dramatic)
// ===========================================
export const CINEMATIC_TIMING = {
    ...DEFAULT_WEB_TIMING,
    cardDeal: 600,
    cardDealDealer: 500,
    cardDealInterval: 100,
    cardFlip: 400,
    blackjack: 1200,
    holeCardReveal: 700,
    bust: 800,
    chipsPaid: 1200,
    chipsCollected: 1200,
    cardsCleared: 700,
    globalSpeedMultiplier: 1.5,
};
export const CINEMATIC_STAGGER_CONFIG = {
    chipPlacement: 50,
    chipPayout: 100,
    chipCollection: 70,
    chipToDealer: 100,
    chipMidRoundBust: 80,
    cardRake: 250,
    sidebetChip: 80,
    insuranceChip: 80,
    doubleDownChip: 100,
    betConfirmChip: 100,
};
export const CINEMATIC_UI_DELAY_CONFIG = {
    insuranceChipWait: 500,
    doubleDownChipWait: 600,
    chipConsolidationSwap: 350,
};
// ===========================================
// GET TIMING FOR EVENT TYPE
// ===========================================
/**
 * Calculate animation duration for a single event.
 * For chip events (CHIPS_PAID, CHIPS_COLLECTED, etc.), calculates actual
 * staggered animation time based on the event's amount field.
 */
export function getEventTiming(event, config = DEFAULT_WEB_TIMING, stagger = DEFAULT_STAGGER_CONFIG) {
    const multiplier = config.globalSpeedMultiplier;
    switch (event.type) {
        // Betting
        case 'BET_STAGED':
            return config.betStaged * multiplier;
        case 'BET_CONFIRMED': {
            // Staggered chip animation from rail to kitty
            const betAmount = event.amount ?? 0;
            const chipCount = getChipCount(betAmount);
            if (chipCount <= 1)
                return config.betConfirmed * multiplier;
            return (config.chipFlight + (chipCount - 1) * stagger.betConfirmChip) * multiplier;
        }
        case 'BET_UNSTAGED':
            return config.betUnstaged * multiplier;
        // Dealing
        case 'HAND_BOX_CREATED':
            return config.handBoxAppear * multiplier;
        case 'CARD_DEALT': {
            // Check if dealer card (no seatPosition means dealer)
            const seatPosition = event.seatPosition;
            if (seatPosition === undefined || seatPosition === null) {
                return config.cardDealDealer * multiplier;
            }
            return config.cardDeal * multiplier;
        }
        case 'SCORE_UPDATED':
            return config.scoreUpdate * multiplier;
        // Player Turn
        case 'TURN_STARTED':
            return config.turnIndicatorShow * multiplier;
        case 'ACTION_TAKEN': {
            // STAND has no visual animation - no delay needed (especially during splits)
            const action = event.action;
            if (action === 'stand')
                return 0;
            return config.actionTaken * multiplier;
        }
        case 'HAND_SPLIT':
            return config.splitHands * multiplier;
        // Special Outcomes
        case 'HAND_BUSTED':
            return config.bust * multiplier;
        case 'HAND_BLACKJACK':
            return config.blackjack * multiplier;
        case 'HAND_SURRENDERED':
            return config.surrender * multiplier;
        // Insurance
        case 'INSURANCE_OFFERED':
            return config.insurancePrompt * multiplier;
        case 'INSURANCE_DECISION':
            return config.insuranceDecision * multiplier;
        case 'INSURANCE_RESOLVED':
            return config.insuranceResolve * multiplier;
        // Dealer
        case 'DEALER_REVEALS_HOLE':
            return config.holeCardReveal * multiplier;
        case 'DEALER_HITS':
            return config.dealerHit * multiplier;
        case 'DEALER_STANDS':
            return config.dealerStand * multiplier;
        case 'DEALER_BUSTS':
            return config.dealerBust * multiplier;
        case 'DEALER_BLACKJACK':
            return config.dealerBlackjack * multiplier;
        // Resolution
        case 'HAND_RESOLVED':
            return config.handOutcomeShow * multiplier;
        case 'CHIPS_PAID': {
            // Two-phase animation:
            // 1. Winnings from dealer to kitty (staggered)
            // 2. ALL chips (bet + winnings) from kitty to rail (staggered)
            const payoutAmount = event.amount ?? 0;
            const winningsCount = getChipCount(payoutAmount);
            // For standard 1:1 payout, bet = winnings, so total kitty = 2 * winnings
            // For blackjack 3:2, bet = winnings / 1.5, so total = winnings + bet ≈ 1.67 * winnings
            // Use 2x as conservative estimate (covers most cases)
            const totalKittyCount = winningsCount * 2;
            if (winningsCount <= 1)
                return config.chipsPaid * multiplier;
            // Phase 1: winnings arrive at kitty
            const payoutTime = config.chipFlight + (winningsCount - 1) * stagger.chipPayout;
            // Phase 2: all chips collected to rail
            const collectTime = config.chipFlight + (totalKittyCount - 1) * stagger.chipCollection;
            return (payoutTime + collectTime) * multiplier;
        }
        case 'CHIPS_COLLECTED': {
            // Single phase: bet chips from kitty to dealer
            const betAmount = event.amount ?? 0;
            const chipCount = getChipCount(betAmount);
            if (chipCount <= 1)
                return config.chipsCollected * multiplier;
            return (config.chipFlight + (chipCount - 1) * stagger.chipToDealer) * multiplier;
        }
        // Sidebet
        case 'SIDEBET_PLACED': {
            const amount = event.amount ?? 0;
            const chipCount = getChipCount(amount);
            if (chipCount <= 1)
                return config.sidebetPlaced * multiplier;
            return (config.chipFlight + (chipCount - 1) * stagger.sidebetChip) * multiplier;
        }
        case 'SIDEBET_CHIPS_PAID': {
            // Sidebet winnings from dealer to player
            const amount = event.amount ?? 0;
            const chipCount = getChipCount(amount);
            if (chipCount <= 1)
                return config.sidebetChipsPaid * multiplier;
            return (config.chipFlight + (chipCount - 1) * stagger.chipPayout) * multiplier;
        }
        case 'SIDEBET_CHIPS_COLLECTED': {
            // Sidebet chips collected by dealer
            const amount = event.amount ?? 0;
            const chipCount = getChipCount(amount);
            if (chipCount <= 1)
                return config.sidebetChipsCollected * multiplier;
            return (config.chipFlight + (chipCount - 1) * stagger.chipToDealer) * multiplier;
        }
        // Rake (cards cleared)
        case 'SEAT_RAKE':
            return config.cardsCleared * multiplier;
        // Round
        case 'ROUND_STARTED':
            return config.roundStart * multiplier;
        case 'BETTING_OPENED':
            return config.bettingOpened * multiplier;
        case 'ROUND_COMPLETED':
            return config.roundComplete * multiplier;
        // Events with no animation
        case 'BETTING_CLOSED':
        case 'DEALING_STARTED':
        case 'PLAYER_TURNS_STARTED':
        case 'DEALER_TURN_STARTED':
        case 'TURN_ENDED':
        case 'PREMATURE_RESOLUTION':
        case 'ALL_PLAYER_TURNS_COMPLETE':
        case 'ALL_RESOLUTIONS_COMPLETE':
        case 'BET_TIMEOUT':
        case 'PLAYER_SIT_OUT':
        case 'PLAYER_SIT_IN':
        case 'BETTING_COUNTDOWN':
        case 'PLAYER_KICKED':
        case 'INITIAL_DEAL_COMPLETE':
        case 'INSURANCE_COUNTDOWN':
        case 'INSURANCE_TIMEOUT':
        case 'TURN_COUNTDOWN':
        case 'TURN_TIMEOUT':
        case 'SIDEBET_RESOLVED':
        case 'SIDEBET_REMOVED':
            return 0;
        default:
            return 0;
    }
}
export const DEFAULT_PAUSE_CONFIG = {
    afterDeal: 400,
    afterBlackjackCheck: 200,
    afterBust: 300,
    afterDealerReveal: 400,
    afterResolution: 300, // Reduced from 600 - just enough to see outcome
    betweenRounds: 600, // Reduced from 1500 - snappier autoplay
    clientEventProcessing: 100, // Reduced from 200 - React updates are fast
};
export const DEFAULT_TIMEOUT_CONFIG = {
    betting: 30000, // 30 seconds (20s grace + 10s visible countdown)
    playerTurn: 30000, // 30 seconds (20s grace + 10s visible countdown)
    insurance: 30000, // 30 seconds (20s grace + 10s visible countdown)
};
// ===========================================
// BETTING TIMER CONFIGURATION
// ===========================================
// Server-enforced betting timer with two phases:
// 1. Invisible 15s grace period after round ends
// 2. Visible 10s countdown if not all players have bet
export const BETTING_TIMER_CONFIG = {
    invisiblePhase: 20000, // 20 seconds grace period
    visiblePhase: 10000, // 10 seconds visible countdown
    maxConsecutiveSitOuts: 3, // Kick player after this many consecutive sit-outs
};
// ===========================================
// INSURANCE TIMER CONFIGURATION
// ===========================================
// Server-enforced insurance decision timer with two phases:
// 1. Invisible 15s grace period after insurance is offered
// 2. Visible 10s countdown if player hasn't decided
export const INSURANCE_TIMER_CONFIG = {
    invisiblePhase: 20000, // 20 seconds grace period before countdown starts
    visiblePhase: 10000, // 10 seconds visible countdown
};
// ===========================================
// PLAYER TURN TIMER CONFIGURATION
// ===========================================
// Server-enforced player turn timer with two phases:
// 1. Invisible grace period after turn starts
// 2. Visible countdown (circular around avatar) if player hasn't acted
// Auto-stand on timeout (no kick - betting timer handles AFK kicks)
export const PLAYER_TURN_TIMER_CONFIG = {
    invisiblePhase: 20000, // 20 seconds grace period
    visiblePhase: 10000, // 10 seconds visible countdown
};
export const DEFAULT_SERVER_DELAYS = {
    // Autoplay delays
    autoplayActionDelay: 300, // 300ms between autoplay actions
    insuranceAutoplayDelay: 2500, // 2.5s before auto-declining insurance
    dealerBlackjackReveal: 2000, // 2s for dealer blackjack reveal animation
    // Backpressure control
    roundAckMaxWait: 2000, // 2s max wait for round ACKs
    roundAckPollInterval: 50, // Poll every 50ms
    ackTimeout: 5000, // 5s ACK timeout
    // Payout processing
    payoutTimeout: 1000, // 1s payout timeout
    // Minimum animation time
    minRoundDelay: 2000, // 2s minimum round delay
    // Spectator timeout
    spectatorTimeout: 30 * 60 * 1000, // 30 minutes
};
/**
 * Calculate total animation time for a batch of events.
 * Used by server to pace autoplay - wait this long before starting next round.
 *
 * The calculation sums up all animation durations because events are processed
 * sequentially (each animation must complete before the next event is processed).
 *
 * @param context - Events and optional timing configuration
 * @returns Total milliseconds for all animations to complete
 */
export function calculateBatchAnimationTime(context) {
    const config = context.config ?? DEFAULT_WEB_TIMING;
    const stagger = context.stagger ?? DEFAULT_STAGGER_CONFIG;
    const pauses = context.pauses ?? DEFAULT_PAUSE_CONFIG;
    let totalDuration = 0;
    for (const event of context.events) {
        const eventDuration = getEventTiming(event, config, stagger);
        totalDuration += eventDuration;
    }
    // Add inter-animation buffer for natural pacing
    // This accounts for React rendering time and state updates between animations
    const eventCount = context.events.filter(e => getEventTiming(e, config, stagger) > 0).length;
    const bufferPerEvent = 50; // 50ms buffer per animated event
    totalDuration += eventCount * bufferPerEvent;
    return totalDuration;
}
/**
 * Calculate animation time for resolution phase events.
 * This is the critical path that determines when the next round can start.
 *
 * @param events - Resolution phase events (HAND_RESOLVED, CHIPS_PAID, etc.)
 * @param config - Timing configuration
 * @param pauses - Pause configuration
 * @returns Total milliseconds for resolution animations + between-rounds pause
 */
export function calculateResolutionAnimationTime(events, config = DEFAULT_WEB_TIMING, pauses = DEFAULT_PAUSE_CONFIG, stagger = DEFAULT_STAGGER_CONFIG) {
    // Filter to only resolution-related events
    const resolutionEventTypes = [
        'HAND_RESOLVED',
        'CHIPS_PAID',
        'CHIPS_COLLECTED',
        'SIDEBET_CHIPS_PAID',
        'SIDEBET_CHIPS_COLLECTED',
        'DEALER_REVEALS_HOLE',
        'DEALER_HITS',
        'DEALER_STANDS',
        'DEALER_BUSTS',
        'DEALER_BLACKJACK',
    ];
    const resolutionEvents = events.filter(e => resolutionEventTypes.includes(e.type));
    const animationTime = calculateBatchAnimationTime({
        events: resolutionEvents,
        config,
        stagger,
    });
    // Add post-resolution pause, client event processing buffer, and between-rounds delay
    // The clientEventProcessing buffer ensures non-visual events (ALL_RESOLUTIONS_COMPLETE,
    // ROUND_COMPLETED) have time to process before the next round's events arrive
    return animationTime + pauses.afterResolution + pauses.clientEventProcessing + pauses.betweenRounds;
}
//# sourceMappingURL=animation-timing.js.map